import React from "react";
import { Card, Tag, Space, Typography, Tooltip, Badge } from "antd";
import {
  ClusterOutlined,
  LockOutlined,
  UnlockOutlined,
  ThunderboltOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import moment from "moment";

const { Text, Paragraph } = Typography;

const priorityColors = {
  urgent: "#ff4d4f",
  high: "#fa8c16",
  normal: "#1890ff",
  low: "#8c8c8c",
};

const BatchCard = ({ item: batch, onCardClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: batch.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const earliestDelivery = batch.earliest_delivery;
  const isOverdue = earliestDelivery
    ? moment(earliestDelivery).isBefore(moment(), "day")
    : false;
  const daysRemaining = earliestDelivery
    ? moment(earliestDelivery).diff(moment(), "days")
    : null;

  const priorityScore = batch.priority_score || 0;
  const topPriority =
    priorityScore >= 50 ? "urgent" : priorityScore >= 40 ? "high" : "normal";

  const jobCount = batch.jobs?.length || 0;
  const totalQty = batch.total_quantity || 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onPointerUp={() => {
        if (!isDragging && onCardClick) onCardClick(batch);
      }}
    >
      <Card
        size="small"
        bordered
        style={{
          borderLeft: `3px solid ${priorityColors[topPriority]}`,
          cursor: "pointer",
          background: batch.is_locked ? "#fafafa" : "#fff",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <Space size={4}>
            <ClusterOutlined style={{ color: "#1890ff" }} />
            <Text strong style={{ fontSize: 12, color: "#1890ff" }}>
              {batch.batch_number}
            </Text>
          </Space>
          <Space size={4}>
            {topPriority === "urgent" && (
              <ThunderboltOutlined style={{ color: "#ff4d4f", fontSize: 12 }} />
            )}
            <Tooltip title={batch.is_locked ? "Locked — jobs move together" : "Unlocked — jobs move independently"}>
              {batch.is_locked
                ? <LockOutlined style={{ color: "#8c8c8c", fontSize: 12 }} />
                : <UnlockOutlined style={{ color: "#fa8c16", fontSize: 12 }} />}
            </Tooltip>
          </Space>
        </div>

        {/* RS and ply */}
        <Space size={4} wrap style={{ marginBottom: 4 }}>
          <Tag style={{ fontSize: 10 }}>RS {batch.roll_size}"</Tag>
          {batch.cutting_size && (
            <Tag style={{ fontSize: 10 }}>CS {batch.cutting_size}"</Tag>
          )}
          {batch.flute_type && (
            <Tag color="geekblue" style={{ fontSize: 10 }}>{batch.flute_type} Flute</Tag>
          )}
        </Space>

        {/* Job count + qty */}
        <div style={{ fontSize: 11, color: "#595959", marginBottom: 4 }}>
          <Text style={{ fontSize: 11 }}>
            {jobCount} job{jobCount !== 1 ? "s" : ""} · {totalQty.toLocaleString("en-IN")} pcs
          </Text>
        </div>

        {/* Customer names (up to 2) */}
        {batch.jobs?.length > 0 && (
          <Paragraph
            ellipsis={{ rows: 1 }}
            style={{ fontSize: 11, color: "#595959", marginBottom: 4 }}
          >
            {[...new Set(batch.jobs.map((bj) => bj.job?.order?.customer?.name).filter(Boolean))].join(", ")}
          </Paragraph>
        )}

        {/* Status */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {earliestDelivery ? (
            <Tooltip title={moment(earliestDelivery).format("DD MMM YYYY")}>
              <Space size={4}>
                <ClockCircleOutlined style={{ fontSize: 11 }} />
                <Text
                  type={isOverdue ? "danger" : daysRemaining !== null && daysRemaining <= 2 ? "warning" : "secondary"}
                  style={{ fontSize: 11 }}
                >
                  {isOverdue ? "Overdue" : daysRemaining === 0 ? "Today" : `${daysRemaining}d left`}
                </Text>
              </Space>
            </Tooltip>
          ) : <span />}
          <Badge
            count={priorityScore}
            style={{
              backgroundColor:
                priorityScore >= 50 ? "#f5222d" : priorityScore >= 40 ? "#fa8c16" : "#1890ff",
              fontSize: 9,
            }}
          />
        </div>
      </Card>
    </div>
  );
};

export default BatchCard;
